import {
    createContext,
    useContext,
    useEffect,
    useReducer,
    useMemo,
    useCallback,
} from 'react'

import { editUser, getUser } from '../apiRoutes/userRoutes'

import { useAuth } from './AuthenticationContext'
import { useSocket } from './SocketContext'
import { Grid } from '../components/UniversalComponents'
import { CircularProgress, Typography } from '@mui/material'
import { useToastMessage } from './ToastPopUpContext'
import { getAuthentication } from '../apiRoutes'

type Props = {
    children: React.ReactNode
}

export const GlobalState = createContext({
    globalState: {
        userPreferences: {
            boatManagementTable: [],
            dataTableColumns: {
                devices: {
                    columns: {},
                    density: '',
                },
                engines: {
                    engine: {
                        columns: {},
                        density: '',
                    },
                    engineCalibration: {
                        columns: {},
                        density: '',
                    },
                    engineModel: {
                        columns: {},
                        density: '',
                    },
                    engineManufacturers: {
                        columns: {},
                        density: '',
                    },
                },
                boats: {
                    boatConfig: {
                        columns: {},
                        density: '',
                    },
                    boatModels: {
                        columns: {},
                        density: '',
                    },
                    boatManufacturers: {
                        columns: {},
                        density: '',
                    },
                },
                dealers: {
                    columns: {},
                    density: '',
                },
                signals: {
                    signalManager: {
                        columns: {},
                        density: '',
                    },
                    signalEvents: {
                        columns: {},
                        density: '',
                    },
                    unitOfMeasurement: {
                        columns: {},
                        density: '',
                    },
                    signalProfileManager: {
                        columns: {},
                        density: '',
                    },
                    signalFormulaManager: {
                        columns: {},
                        density: '',
                    },
                },
                subscriptions: {
                    subscriptionProfileManager: {
                        columns: {},
                        density: '',
                    },
                    assignBoatSubscriptions: {
                        columns: {},
                        density: '',
                    },
                },
                system: {
                    osPatchNotes: {
                        columns: {},
                        density: '',
                    },
                    mobilePatchNotes: {
                        columns: {},
                        density: '',
                    },
                },
                users: {
                    userAdmin: {
                        columns: {},
                        density: '',
                    },
                    userNotificationSubscription: {
                        columns: {},
                        density: '',
                    },
                },
                manufacturers: {
                    columns: {},
                    density: '',
                },
            },
        },
        oupUser: {
            activeManufacturer: { _id: '', manufacturerName: '' },
            _id: '',
            dealerProfile: '',
            userNotifications: [],
            userNotificationsArchived: [],
            userBoats: [],
            userProfile: {
                superAdmin: false,
                userProfilePermissions: {
                    Manufacturer: {
                        read: false,
                        write: false,
                    },
                    Users: {
                        read: false,
                        write: false,
                    },
                },
                dealerProfile: false,
                userProfileAssignedManufacturers: [],
            },
            assignedDealerProfile: {
                _id: '',
                dealers: [],
                manufacturers: [],
            },
            userEmail: '',
        },
        userProfile: {
            superAdmin: false,
            userProfilePermissions: {
                Users: {
                    read: false,
                    write: false,
                },
            },
            dealerProfile: false,
            userProfileAssignedManufacturers: [],
        },
    },

    setGlobalState: (values: any) => {},
    setUser: (param: any) => {},
})

export const GlobalStateContext = ({ children }: Props) => {
    const { auth, setAuth, emulation, isEmulating } = useAuth() as any
    const { authenticated, _id, loading: authLoading, signingIn } = auth

    const { apiSocket } = useSocket()

    const defaultState = {
        userPreferences: {},
        userRole: '',
        userPermissions: {},
        activeManufacturer: { manufacturerName: '' },
        manufacturer: [],
        _id: '',
        username: '',
        oupUser: {
            activeManufacturer: { _id: '', manufacturerName: '' },
            userProfile: {
                superAdmin: false,
                userProfilePermissions: {},
                userProfileAssignedManufacturers: [],
            },
            userEmail: '',
        },
        userProfile: { superAdmin: false },
        loading: true,
        update: false,
    }

    const reducer = (oldState: any, newState: any) => ({
        ...oldState,
        ...newState,
    })
    const [state, setState] = useReducer(reducer, defaultState)

    const { oupUser, loading, update } = state
    const setToastMessage = useToastMessage()

    const getData = async () => {
        // Never call /user with an empty id: getData fires from several effects
        // and can race ahead of auth._id being set. The backend returns its
        // errors with a no-op `.status(400)` (so they arrive as HTTP 200 + an
        // error STRING), and getUser then hands that string back as "data".
        if (!auth._id) return
        try {
            const userData = await getUser({ _id: auth._id })
            if (userData && userData._id) {
                setState({ oupUser: userData, loading: false })
            } else {
                // Non-result (or the 200 error-string): clear loading but do NOT
                // spread stale `state` — that would clobber a good oupUser that a
                // parallel getData run already set.
                setState({ loading: false })
            }
        } catch (err: any) {
            console.error('Global state fetch error:', err.message)
            setState({ loading: false, error: err.message })
        }
    }

    const setGlobalState = useCallback(
        (values: any) => setState(values),
        []
    )

    const getActiveManufacturerUpdate = async () => {
        try {
            const currentSession = await getAuthentication({
                _id,
            })

            if (currentSession) {
                setAuth({
                    ...currentSession,
                    authenticated: true,
                })
            } else {
                throw new Error('No user found')
            }
        } catch (error) {
            console.error('error')
        }
    }

    interface SetUserParams {
        key: string
        value: any
        type?: string
    }
    const setUser = async ({ key, value, type }: SetUserParams) => {
        try {
            let parsedFormData = {}

            if (!type) {
                parsedFormData = {
                    id: _id,
                    // ...state.oupUser,
                    [key]: value,
                }
            } else if (type === 'columnData') {
                parsedFormData = {
                    id: _id,
                    // ...state.oupUser,
                    userPreferences: {
                        ...state.oupUser.userPreferences,
                        dataTableColumns: {
                            ...state.oupUser.userPreferences?.dataTableColumns,
                            [key]: {
                                ...state.oupUser.userPreferences
                                    .dataTableColumns?.[key],
                                ...value,
                            },
                        },
                    },
                }
            }

            // console.log(parsedFormData, 'formData')
            let res: any

            res = await editUser(parsedFormData)
            if (res) {
                const { data, status } = res

                if (data && status === 200) {
                    getActiveManufacturerUpdate()
                    setState({ oupUser: oupUser, update: true })

                    setToastMessage(
                        `User: ${data.userEmail} preferences were successfully edited`,
                        'success'
                    )

                    // getData()
                } else {
                    throw new Error('User edit was unsuccessfull')
                }
            }
        } catch (err: any) {
            console.error(err, 'error')
            setToastMessage(`${err.message}`, 'error')
        }
    }

    useEffect(() => {
        if (auth._id && !auth.loading && !signingIn) {
            getData()
        } else if (!auth.loading && !authenticated) {
            setState({
                loading: false,
            })
        }
    }, [auth, auth._id])

    useEffect(() => {
        if (authenticated && _id && apiSocket) {
            const handleUserUpdate = () => {
                getActiveManufacturerUpdate()
            }
            apiSocket.on('update-user', handleUserUpdate)
            // Clean up so listeners don't accumulate when this effect re-runs.
            return () => {
                apiSocket.off('update-user', handleUserUpdate)
            }
        }
    }, [authenticated, _id, apiSocket])

    useEffect(() => {
        if (auth._id && !auth.loading && signingIn === true) {
            // reducer already merges; avoid spreading stale `state`
            setState({ loading: true, error: null })
            getData()
        } else if (!auth.loading && !authenticated) {
            setState({
                loading: false,
            })
        }
    }, [signingIn])

    // Impersonation: overlay oupUser to faithfully present the impersonated
    // manufacturer / dealer-profile (this drives the dashboard + anything that
    // reads identity from globalState). Real super admin gating happens upstream.
    const effectiveOupUser = useMemo(() => {
        if (!isEmulating || !emulation) return oupUser
        if (emulation.type === 'manufacturer') {
            return {
                ...oupUser,
                activeManufacturer: {
                    _id: emulation.id,
                    manufacturerName: emulation.name,
                },
                userProfile: {
                    ...oupUser.userProfile,
                    superAdmin: false,
                    dealerProfile: false,
                    role: 'MANUFACTURER',
                    userProfileAssignedManufacturers: [emulation.id],
                },
                assignedDealerProfile: { _id: '', dealers: [], manufacturers: [] },
            }
        }
        // dealer profile
        return {
            ...oupUser,
            userProfile: {
                ...oupUser.userProfile,
                superAdmin: false,
                dealerProfile: true,
                role: 'DEALER',
            },
            assignedDealerProfile: {
                _id: emulation.id,
                dealerProfileName: emulation.name,
                dealers: emulation.dealers || [],
                manufacturers: emulation.manufacturers || [],
            },
        }
    }, [oupUser, emulation, isEmulating])

    const effectiveState = useMemo(
        () =>
            isEmulating
                ? { ...state, oupUser: effectiveOupUser, realOupUser: oupUser }
                : state,
        [state, effectiveOupUser, oupUser, isEmulating]
    )

    // NOTE: all hooks must run before any early return, or React throws
    // "Rendered more hooks than during the previous render".
    const value = useMemo(
        () => ({
            globalState: effectiveState,
            setUser,
            setGlobalState,
        }),
        [effectiveState, _id, setGlobalState]
    )

    if (loading || authLoading) {
        return (
            <Grid item xs={12} md={12} lg={12}>
                <Typography align="center" sx={{ marginTop: 40 }}>
                    <CircularProgress />
                </Typography>
            </Grid>
        )
    }

    return (
        <GlobalState.Provider value={value}>{children}</GlobalState.Provider>
    )
}

export const useGlobalState = () => useContext(GlobalState)
